import { useState, useEffect, useCallback, useRef } from "react";
import { useSocket } from "./socket";
import { ChatService } from "@/services/chat.service";
import { Conversation, Message, SocketTypingPayload, SocketReadPayload, SendMessageDto, SocketReactionPayload, SocketUserStatusPayload } from "@/types/chat";
import { useAuth } from "./auth";

let unreadCount = 0;
let originalTitle = typeof document !== 'undefined' ? document.title : "Chat";

const playNotificationSound = () => {
  try {
    const audio = new Audio("/chime.mp3");
    audio.play().catch(e => console.error("Audio play failed", e));
  } catch (e) {
    console.error("Audio play failed", e);
  }
};

export const useChat = () => {
  const { socket } = useSocket();
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationIds, setActiveConversationIds] = useState<string[]>([]);
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  const [typingUsers, setTypingUsers] = useState<Record<string, string[]>>({}); // conversationId -> userIds
  const [loading, setLoading] = useState(true);
  
  // Pagination tracking
  const [cursors, setCursors] = useState<Record<string, string | undefined>>({});
  const [hasMore, setHasMore] = useState<Record<string, boolean>>({});
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const fetchedConversationsRef = useRef<Set<string>>(new Set());

  // Fetch conversations on load
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const data = await ChatService.getConversations();
        setConversations(data);
        
        // Join all conversation rooms to receive background notifications
        if (socket) {
          data.forEach((conv) => socket.emit("chat:join", conv.id));
        }
      } catch (err) {
        console.error("Failed to load conversations", err);
      } finally {
        setLoading(false);
      }
    };
    fetchConversations();
  }, [socket]); // depend on socket so it joins when socket connects

  // Handle reconnections
  useEffect(() => {
    if (!socket) return;
    
    const handleConnect = () => {
      conversations.forEach((conv) => socket.emit("chat:join", conv.id));
      activeConversationIds.forEach(id => socket.emit("chat:join", id));
    };
    
    socket.on("connect", handleConnect);
    return () => {
      socket.off("connect", handleConnect);
    };
  }, [socket, conversations, activeConversationIds]);

  // Handle active conversation selection
  useEffect(() => {
    if (!socket) return;

    activeConversationIds.forEach(activeId => {
      // Join room just in case it's a new one not in the initial fetch
      socket.emit("chat:join", activeId);

      // Fetch initial messages if not already loaded fully
      if (!fetchedConversationsRef.current.has(activeId)) {
        ChatService.getMessages(activeId)
          .then(({ messages: initialMessages, nextCursor }) => {
            setMessages((prev) => {
              // Merge in case we received a realtime message while closed
              const existing = prev[activeId] || [];
              const existingIds = new Set(existing.map(m => m.id));
              const newFetched = initialMessages.reverse().filter(m => !existingIds.has(m.id));
              return {
                ...prev,
                [activeId]: [...newFetched, ...existing],
              };
            });
            
            setCursors(prev => ({ ...prev, [activeId]: nextCursor }));
            setHasMore(prev => ({ ...prev, [activeId]: !!nextCursor }));
            fetchedConversationsRef.current.add(activeId);
          })
          .catch((err) => console.error("Failed to load messages", err));
      }
    });
    
    // Deliberately NOT leaving the room on unmount so we continue to receive background notifications
  }, [socket, activeConversationIds]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    const handleReceiveMessage = (message: Message) => {
      setMessages((prev) => {
        const conversationMessages = prev[message.conversationId] || [];
        
        // If it's our own message bouncing back with tempId, replace the optimistic one
        if (message.tempId) {
          const hasOptimistic = conversationMessages.some((msg) => msg.tempId === message.tempId);
          if (hasOptimistic) {
            return {
              ...prev,
              [message.conversationId]: conversationMessages.map((msg) =>
                msg.tempId === message.tempId ? message : msg
              ),
            };
          }
        } else if (message.senderId === user?.id) {
          // It might be a file upload broadcast from another tab OR from this tab via HTTP
          // Let's see if we have an optimistic message that matches type and filename
          const optimisticMatch = conversationMessages.find(m => 
            m.tempId && 
            m.type === message.type && 
            m.content === message.content &&
            m.attachments?.[0]?.fileName === message.attachments?.[0]?.fileName
          );
          if (optimisticMatch) {
            return {
              ...prev,
              [message.conversationId]: conversationMessages.map(m => m.tempId === optimisticMatch.tempId ? message : m)
            };
          }
        }

        // Avoid duplicates
        if (conversationMessages.some((msg) => msg.id === message.id)) return prev;

        // Notification logic for background tab
        if (document.hidden && message.senderId !== user?.id) {
          unreadCount++;
          document.title = `(${unreadCount}) New Message!`;
          playNotificationSound();
        }

        return {
          ...prev,
          [message.conversationId]: [...conversationMessages, message],
        };
      });

      // Update conversation list
      setConversations((prev) => {
        return prev.map((conv) => {
          if (conv.id === message.conversationId) {
            const hasOptimistic = conv.messages?.some((msg) => msg.tempId && msg.tempId === message.tempId);
            let newMessages = conv.messages || [];
            if (message.tempId && hasOptimistic) {
                newMessages = newMessages.map(m => m.tempId === message.tempId ? message : m);
            } else if (!newMessages.some(m => m.id === message.id)) {
                newMessages = [...newMessages, message];
            }

            return {
              ...conv,
              messages: newMessages,
              unreadCount: !activeConversationIds.includes(conv.id) && message.senderId !== user?.id ? (conv.unreadCount || 0) + 1 : 0,
              lastMessageAt: message.createdAt,
            };
          }
          return conv;
        }).sort((a, b) => new Date(b.lastMessageAt || b.updatedAt).getTime() - new Date(a.lastMessageAt || a.updatedAt).getTime());
      });

      if (activeConversationIds.includes(message.conversationId) && message.senderId !== user?.id) {
        socket.emit("chat:read", { conversationId: message.conversationId, messageId: message.id });
      }
    };

    const handleTyping = (payload: SocketTypingPayload) => {
      setTypingUsers((prev) => {
        const currentTyping = prev[payload.conversationId] || [];
        if (payload.isTyping) {
          if (!currentTyping.includes(payload.userId)) {
            return { ...prev, [payload.conversationId]: [...currentTyping, payload.userId] };
          }
        } else {
          return {
            ...prev,
            [payload.conversationId]: currentTyping.filter((id) => id !== payload.userId),
          };
        }
        return prev;
      });
    };

    const handleReadReceipt = (payload: SocketReadPayload) => {
      setMessages((prev) => {
        const conversationMessages = prev[payload.conversationId] || [];
        return {
          ...prev,
          [payload.conversationId]: conversationMessages.map((msg) => {
            // Very basic read receipt logic: mark the specific message and potentially all older ones as read
            if (msg.id === payload.messageId || new Date(msg.createdAt) <= new Date(payload.readAt)) {
              return { ...msg, readAt: payload.readAt };
            }
            return msg;
          }),
        };
      });
    };

    const handleReaction = (payload: SocketReactionPayload) => {
      setMessages((prev) => {
        const conversationMessages = prev[payload.conversationId] || [];
        return {
          ...prev,
          [payload.conversationId]: conversationMessages.map((msg) => {
            if (msg.id === payload.messageId) {
              let newReactions = [...(msg.reactions || [])];
              if (payload.action === "add") {
                // Prevent duplicate reactions from same user
                if (!newReactions.some(r => r.userId === payload.userId && r.emoji === payload.emoji)) {
                  newReactions.push({ userId: payload.userId, emoji: payload.emoji });
                }
              } else {
                newReactions = newReactions.filter(r => !(r.userId === payload.userId && r.emoji === payload.emoji));
              }
              return { ...msg, reactions: newReactions };
            }
            return msg;
          }),
        };
      });
    };

    const handleUserStatus = (payload: SocketUserStatusPayload) => {
      setConversations((prev) => 
        prev.map((conv) => {
          let updated = false;
          const newParticipants = conv.participants.map(p => {
            if (p.id === payload.userId) {
              updated = true;
              return { ...p, isOnline: payload.isOnline, lastSeenAt: payload.lastSeenAt };
            }
            return p;
          });
          return updated ? { ...conv, participants: newParticipants } : conv;
        })
      );
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        unreadCount = 0;
        document.title = originalTitle;
      }
    };

    const handleParticipantsAdded = (payload: { conversationId: string; conversation: Conversation }) => {
      setConversations((prev) => prev.map(c => c.id === payload.conversationId ? payload.conversation : c));
    };

    const handleParticipantRemoved = (payload: { conversationId: string; userId: string; conversation: Conversation }) => {
      setConversations((prev) => prev.map(c => c.id === payload.conversationId ? payload.conversation : c));
      // If the removed user is us, we should remove the conversation
      if (payload.userId === user?.id) {
        setConversations(prev => prev.filter(c => c.id !== payload.conversationId));
        closeConversation(payload.conversationId);
      }
    };

    const handleConversationDeleted = (payload: { conversationId: string }) => {
      setConversations(prev => prev.filter(c => c.id !== payload.conversationId));
      closeConversation(payload.conversationId);
    };

    const handleAddedToGroup = (payload: { conversation: Conversation }) => {
      setConversations(prev => {
        if (prev.some(c => c.id === payload.conversation.id)) return prev;
        return [payload.conversation, ...prev];
      });
      if (socket) socket.emit("chat:join", payload.conversation.id);
    };

    socket.on("chat:receiveMessage", handleReceiveMessage);
    socket.on("chat:typing", handleTyping);
    socket.on("chat:readReceipt", handleReadReceipt);
    socket.on("chat:reaction", handleReaction);
    socket.on("user:status", handleUserStatus);
    socket.on("chat:participantsAdded", handleParticipantsAdded);
    socket.on("chat:participantRemoved", handleParticipantRemoved);
    socket.on("chat:conversationDeleted", handleConversationDeleted);
    socket.on("chat:addedToGroup", handleAddedToGroup);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      socket.off("chat:receiveMessage", handleReceiveMessage);
      socket.off("chat:typing", handleTyping);
      socket.off("chat:readReceipt", handleReadReceipt);
      socket.off("chat:reaction", handleReaction);
      socket.off("user:status", handleUserStatus);
      socket.off("chat:participantsAdded", handleParticipantsAdded);
      socket.off("chat:participantRemoved", handleParticipantRemoved);
      socket.off("chat:conversationDeleted", handleConversationDeleted);
      socket.off("chat:addedToGroup", handleAddedToGroup);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [socket, activeConversationIds, user?.id]);

  const sendMessage = useCallback(
    async (
      conversationId: string, 
      content: string, 
      file?: File | null, 
      onProgress?: (progress: number) => void
    ) => {
      if (!user) return;

      const tempId = Date.now().toString();
      let type: "TEXT" | "IMAGE" | "FILE" = "TEXT";
      let localUrl = "";

      if (file) {
        type = file.type.startsWith("image/") ? "IMAGE" : "FILE";
        localUrl = URL.createObjectURL(file);
      }

      const optimisticMessage: Message = {
        id: tempId,
        tempId,
        conversationId,
        senderId: user.id as string,
        content,
        type,
        createdAt: new Date().toISOString(),
        ...(file && {
          attachments: [
            {
              id: tempId,
              fileUrl: localUrl,
              fileType: file.type,
              fileName: file.name,
              fileSize: file.size
            }
          ]
        })
      };

      // Optimistic update
      setMessages((prev) => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), optimisticMessage],
      }));

      try {
        if (file) {
          const formData = new FormData();
          formData.append("content", content);
          formData.append("type", type);
          formData.append("files", file);

          const newMessage = await ChatService.sendMessage(conversationId, formData, onProgress);
          
          setMessages((prev) => {
            const conversationMessages = prev[conversationId] || [];
            return {
              ...prev,
              [conversationId]: conversationMessages
                .map((msg) => msg.tempId === tempId ? newMessage : msg)
                // Filter out the duplicate if socket arrived first
                .filter((msg, index, self) => index === self.findIndex(m => m.id === msg.id))
            };
          });
        } else {
          // Send plain text to socket for instant delivery
          if (socket) {
            socket.emit("chat:message", {
              conversationId,
              content,
              type,
              tempId,
            });
          }
        }
      } catch (err) {
        console.error("Failed to send message", err);
      }
    },
    [socket, user]
  );



  const reactToMessage = useCallback(
    (conversationId: string, messageId: string, emoji: string, action: "add" | "remove") => {
      if (!socket || !user) return;
      
      // Optimistic update
      setMessages((prev) => {
        const conversationMessages = prev[conversationId] || [];
        return {
          ...prev,
          [conversationId]: conversationMessages.map((msg) => {
            if (msg.id === messageId) {
              let newReactions = [...(msg.reactions || [])];
              if (action === "add") {
                if (!newReactions.some(r => r.userId === user.id && r.emoji === emoji)) {
                  newReactions.push({ userId: user.id, emoji });
                }
              } else {
                newReactions = newReactions.filter(r => !(r.userId === user.id && r.emoji === emoji));
              }
              return { ...msg, reactions: newReactions };
            }
            return msg;
          }),
        };
      });

      // Send via HTTP API to ensure it's saved reliably (and it will broadcast back over socket to others)
      ChatService.reactToMessage(messageId, emoji, action).catch(console.error);
    },
    [socket, user]
  );

  const setTypingStatus = useCallback(
    (conversationId: string, isTyping: boolean) => {
      if (!socket) return;
      socket.emit("chat:typing", { conversationId, isTyping });
    },
    [socket]
  );

  const openConversation = useCallback((conversationId: string) => {
    setActiveConversationIds(prev => {
      if (prev.includes(conversationId)) return prev;
      return [...prev, conversationId].slice(-3); // Keep max 3 open
    });
    
    // Clear unread count locally
    setConversations((prev) => prev.map(c => c.id === conversationId ? { ...c, unreadCount: 0 } : c));
    
    // Mark as read on server side
    ChatService.markAsRead(conversationId).catch(console.error);
  }, []);

  const closeConversation = useCallback((conversationId: string) => {
    setActiveConversationIds(prev => prev.filter(id => id !== conversationId));
  }, []);

  const startConversationWithUser = useCallback(async (targetUserId: string) => {
    if (!user) return;
    
    // Check if we already have a direct conversation with this user
    const existingConv = conversations.find(
      (c) => c.type === "DIRECT" && c.participants.some((p) => p.id === targetUserId)
    );

    if (existingConv) {
      openConversation(existingConv.id);
      return;
    }

    try {
      // Create new conversation
      const newConv = await ChatService.createConversation({
        type: "DIRECT",
        participantIds: [targetUserId],
      });
      
      setConversations((prev) => [newConv, ...prev]);
      if (socket) socket.emit("chat:join", newConv.id);
      openConversation(newConv.id);
    } catch (err) {
      console.error("Failed to create conversation", err);
    }
  }, [conversations, openConversation, user, socket]);

  const startGroupConversation = useCallback(async (name: string, description: string, participantIds: string[]) => {
    if (!user) return;
    try {
      const newConv = await ChatService.createConversation({
        type: "GROUP",
        name,
        description,
        participantIds,
      });
      
      setConversations((prev) => [newConv, ...prev]);
      if (socket) socket.emit("chat:join", newConv.id);
      openConversation(newConv.id);
    } catch (err) {
      console.error("Failed to create group conversation", err);
      throw err;
    }
  }, [openConversation, user, socket]);

  const addGroupMembers = useCallback(async (conversationId: string, participantIds: string[]) => {
    if (!user) return;
    try {
      const updatedConv = await ChatService.addParticipants(conversationId, participantIds);
      setConversations(prev => prev.map(c => c.id === conversationId ? { ...updatedConv, unreadCount: c.unreadCount } : c));
    } catch (err) {
      console.error("Failed to add members", err);
      throw err;
    }
  }, [user]);

  const removeGroupMember = useCallback(async (conversationId: string, userId: string) => {
    if (!user) return;
    try {
      const updatedConv = await ChatService.removeParticipant(conversationId, userId);
      setConversations(prev => prev.map(c => c.id === conversationId ? { ...updatedConv, unreadCount: c.unreadCount } : c));
    } catch (err) {
      console.error("Failed to remove member", err);
      throw err;
    }
  }, [user]);

  const deleteGroupConversation = useCallback(async (conversationId: string) => {
    if (!user) return;
    try {
      await ChatService.deleteConversation(conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      closeConversation(conversationId);
    } catch (err) {
      console.error("Failed to delete conversation", err);
      throw err;
    }
  }, [user, closeConversation]);

  const loadMoreMessages = useCallback(async (conversationId: string) => {
    if (isFetchingMore || !hasMore[conversationId]) return;
    
    setIsFetchingMore(true);
    const cursor = cursors[conversationId];
    
    try {
      const { messages: olderMessages, nextCursor } = await ChatService.getMessages(conversationId, 20, cursor);
      
      setMessages((prev) => {
        const existing = prev[conversationId] || [];
        const existingIds = new Set(existing.map(m => m.id));
        const toPrepend = olderMessages.reverse().filter(m => !existingIds.has(m.id));
        
        return {
          ...prev,
          [conversationId]: [...toPrepend, ...existing],
        };
      });
      
      setCursors(prev => ({ ...prev, [conversationId]: nextCursor }));
      setHasMore(prev => ({ ...prev, [conversationId]: !!nextCursor }));
    } catch (err) {
      console.error("Failed to load older messages", err);
    } finally {
      setIsFetchingMore(false);
    }
  }, [cursors, hasMore, isFetchingMore]);

  return {
    conversations,
    activeConversationIds,
    messages,
    typingUsers,
    loading,
    sendMessage,
    reactToMessage,
    setTypingStatus,
    openConversation,
    closeConversation,
    startConversationWithUser,
    startGroupConversation,
    addGroupMembers,
    removeGroupMember,
    deleteGroupConversation,
    loadMoreMessages,
    hasMore,
    isFetchingMore,
  };
};
